import { useState, useCallback } from 'react';

/**
 * Built-in validators
 */
const validators = {
  required: (value) => {
    if (value === null || value === undefined || value === '') {
      return 'validation.required';
    }
    if (typeof value === 'string' && value.trim() === '') {
      return 'validation.required';
    }
    return null;
  },

  email: (value) => {
    if (!value) return null; // Let required handle empty values
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'validation.invalid_email';
    }
    return null;
  },

  minLength: (min) => (value) => {
    if (!value) return null;
    if (value.length < min) {
      return { key: 'validation.min_length', params: { min } };
    }
    return null;
  },

  maxLength: (max) => (value) => {
    if (!value) return null;
    if (value.length > max) {
      return { key: 'validation.max_length', params: { max } };
    }
    return null;
  },

  matches: (fieldName, fieldLabel) => (value, allValues) => {
    if (!value) return null;
    if (value !== allValues[fieldName]) {
      return { key: 'validation.passwords_must_match', params: { field: fieldLabel } };
    }
    return null;
  },

  pattern: (regex, message) => (value) => {
    if (!value) return null;
    if (!regex.test(value)) {
      return message || 'validation.invalid_format';
    }
    return null;
  },
};

/**
 * Custom hook for form validation
 * Supports on-blur validation and full form validation on submit
 *
 * @returns {Object} Validation utilities and state
 */
export function useFormValidation() {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * Validates a single field against its rules
   * @param {string} name - Field name
   * @param {any} value - Field value
   * @param {Array} rules - Array of validator functions
   * @param {Object} allValues - All form values (for cross-field validation)
   * @returns {string|Object|null} - Error message key or null if valid
   */
  const validateField = useCallback((name, value, rules = [], allValues = {}) => {
    for (const rule of rules) {
      const error = typeof rule === 'function' ? rule(value, allValues) : null;
      if (error) {
        return error;
      }
    }
    return null;
  }, []);

  /**
   * Handles blur event for a field - validates and sets touched state
   * @param {string} name - Field name
   * @param {any} value - Field value
   * @param {Array} rules - Array of validator functions
   * @param {Object} allValues - All form values
   */
  const handleBlur = useCallback((name, value, rules = [], allValues = {}) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value, rules, allValues);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  }, [validateField]);

  /**
   * Clears error for a specific field (call when user starts typing)
   * @param {string} name - Field name
   */
  const clearFieldError = useCallback((name) => {
    setErrors((prev) => {
      if (prev[name]) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  /**
   * Validates entire form
   * @param {Object} data - Form data object
   * @param {Object} allRules - Object mapping field names to their validation rules
   * @returns {boolean} - True if form is valid, false otherwise
   */
  const validateForm = useCallback((data, allRules) => {
    const newErrors = {};
    let isValid = true;

    for (const [fieldName, rules] of Object.entries(allRules)) {
      const error = validateField(fieldName, data[fieldName], rules, data);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    }

    // Mark all fields as touched
    const allTouched = Object.keys(allRules).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    setErrors(newErrors);

    return isValid;
  }, [validateField]);

  /**
   * Resets all validation state
   */
  const resetValidation = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  /**
   * Gets error message for a field (returns null if not touched)
   * @param {string} name - Field name
   * @param {Function} t - i18n translation function
   * @returns {string|null} - Error message or null
   */
  const getFieldError = useCallback((name, t) => {
    if (!touched[name] || !errors[name]) {
      return null;
    }

    const error = errors[name];

    if (typeof error === 'string') {
      return t ? t(error) : error;
    }

    if (error && typeof error === 'object' && error.key) {
      return t ? t(error.key, error.params) : error.key;
    }

    return null;
  }, [errors, touched]);

  /**
   * Checks if a field has an error (regardless of touched state)
   * @param {string} name - Field name
   * @returns {boolean}
   */
  const hasError = useCallback((name) => {
    return touched[name] && !!errors[name];
  }, [errors, touched]);

  return {
    errors,
    touched,
    validateField,
    handleBlur,
    clearFieldError,
    validateForm,
    resetValidation,
    getFieldError,
    hasError,
    validators,
  };
}

// Export validators for direct use
export { validators };

export default useFormValidation;
